import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutGrid,
  ListChecks,
  Sparkles,
  Image as ImageIcon,
  Library,
  Receipt,
  BarChart3,
  Settings as SettingsIcon,
  Plug,
} from 'lucide-react';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { useStore } from '../../store';
import { Dot } from '../ui/Badge';
import { Toaster } from '../ui/Toaster';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutGrid, kicker: '01' },
  { to: '/listings', label: 'Listings', icon: ListChecks, kicker: '02' },
  { to: '/draft-studio', label: 'Draft Studio', icon: Sparkles, kicker: '03' },
  { to: '/image-lab', label: 'Image Lab', icon: ImageIcon, kicker: '04' },
  { to: '/content', label: 'Content', icon: Library, kicker: '05' },
  { to: '/orders', label: 'Orders', icon: Receipt, kicker: '06' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, kicker: '07' },
  { to: '/settings', label: 'Settings', icon: SettingsIcon, kicker: '08' },
];

export function AppShell({ children }: { children: ReactNode }) {
  const auth = useStore((s) => s.auth);
  const location = useLocation();
  const current = NAV.find((n) => n.to === location.pathname) ?? NAV[0];

  return (
    <div className="min-h-screen text-ink-800 flex">
      {/* Sidebar — warm cream, generous radius, sticky logo */}
      <aside className="w-64 shrink-0 bg-cream border-r border-cream-300 flex flex-col">
        <div className="px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="relative h-10 w-10 rounded-2xl bg-gradient-to-br from-etsy-400 to-etsy-600 flex items-center justify-center shadow-[0_8px_20px_-6px_rgba(241,100,30,0.55)]">
              <span className="font-display text-[18px] font-semibold text-white leading-none">E</span>
              <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-etsy-400/20 to-etsy-600/0 blur" />
            </div>
            <div>
              <div className="text-[15px] font-semibold tracking-tight text-ink-800 leading-tight">Etsy Studio</div>
              <div className="kicker mt-0.5 text-ink-400">v0.1 · local</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-2 space-y-0.5">
          {NAV.map((n) => {
            const active = n.to === current.to;
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={clsx(
                  'group flex items-center gap-3 px-3 py-2 rounded-xl text-[13px] transition relative',
                  active
                    ? 'bg-white text-ink-800 shadow-[0_2px_8px_-2px_rgba(80,50,20,0.08),0_1px_2px_rgba(80,50,20,0.04)] border border-cream-300'
                    : 'text-ink-500 hover:text-ink-800 hover:bg-cream-200/60',
                )}
              >
                {active && (
                  <motion.div
                    layoutId="active-nav"
                    className="absolute left-0 top-2 bottom-2 w-[3px] bg-etsy rounded-r-full"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
                <Icon
                  size={15}
                  strokeWidth={active ? 2.2 : 1.7}
                  className={active ? 'text-etsy' : 'text-ink-400 group-hover:text-ink-600'}
                />
                <span className="flex-1 tracking-tight">{n.label}</span>
                <span className="kicker text-[9px] opacity-50 group-hover:opacity-80">{n.kicker}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-4">
          <ConnectionPill auth={auth} />
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-cream-300 bg-cream-50/70 backdrop-blur-md flex items-center px-8 gap-4">
          <div className="flex items-center gap-2 text-[12.5px]">
            <span className="kicker text-ink-400">Etsy Studio</span>
            <span className="text-cream-300">/</span>
            <span className="text-ink-800 font-medium tracking-tight">{current.label}</span>
          </div>
          <div className="flex-1" />
          <button className="btn btn-ghost h-8 px-3 text-[12.5px] gap-2">
            <kbd className="font-mono text-[10px] text-ink-400 bg-cream-200 border border-cream-300 px-1.5 py-0.5 rounded">⌘K</kbd>
            <span>Search</span>
          </button>
          <div className="h-6 w-px bg-cream-300" />
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-etsy-300 to-etsy-500 flex items-center justify-center text-white text-[11px] font-semibold">P</div>
            <span className="text-[12.5px] text-ink-600 font-medium tracking-tight">Pavel</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto scroll-soft">
          <div className="max-w-[1400px] mx-auto px-8 py-7 page-enter">{children}</div>
        </main>
      </div>

      <Toaster />
    </div>
  );
}

function ConnectionPill({ auth }: { auth: { connected: boolean; shop_name: string | null } | null }) {
  const connected = auth?.connected;
  return (
    <div className="bg-white border border-cream-300 rounded-2xl p-3 flex items-center gap-3 shadow-[0_2px_8px_-2px_rgba(80,50,20,0.06)]">
      <div className="h-9 w-9 rounded-xl bg-cream-200 flex items-center justify-center">
        <Plug size={14} className="text-ink-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <Dot tone={connected ? 'success' : 'warn'} />
          <span className="text-[12.5px] font-medium text-ink-800 truncate">
            {connected ? auth?.shop_name ?? 'Connected' : 'Not connected'}
          </span>
        </div>
        <div className="kicker mt-0.5 text-ink-400">
          {connected ? 'OAUTH OK' : 'SET ETSY KEY'}
        </div>
      </div>
    </div>
  );
}
